import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InspectionCategoryCacheService } from '../core/cache/inspection-category-cache-service';
import { EnumCacheService } from '../core/cache/enum-cache-service';
import { InspectionDBService } from '../core/db/inspection-db-service';
import { TranslateService, TranslateDirective } from '@ngx-translate/core';
import { NgForm, FormsModule } from '@angular/forms';
import { MkdeAlertHolderDirective } from '../alert/mkde-alert-holder.directive';
import { StatusChangeValidationService } from '../core/status-change-validation.service';
import { StatusChangeDialogComponent } from '../status-change-dialog/status-change-dialog.component';
import { I18NText } from '../core/api/model/i18NText';
import { InspectionElementDto } from '../core/api/model/inspectionElementDto';
import { InspectionStatusDto } from '../core/api/model/inspectionStatusDto';
import { InspectionStyleDto } from '../core/api/model/inspectionStyleDto';
import { InspectionWithResultsDto } from '../core/api/model/inspectionWithResultsDto';
import { InspectionStatus } from '../core/api/model/inspectionStatus';
import { keysToTranslate } from '@core/util/keys-to-translate';
import { FormColumnsDirective } from '../form/form-columns.directive';
import { FormRowDirective } from '../form/form-row.directive';
import { FormControlComponent } from '../form/form-control.component';
import { MkdeDatepickerComponent } from '../datepicker/mkde-datepicker.component';
import { AutofocusDirective } from '../autofocus/autofocus.directive';
import { I18nTextPipe } from '../core/pipes/i18n-text.pipe';
import { I18nTextListPipe } from '../core/pipes/i18n-text-list.pipe';

@Component({
  selector: 'app-inspection-dialog',
  templateUrl: './inspection-dialog.component.html',
  imports: [
    TranslateDirective,
    FormsModule,
    FormColumnsDirective,
    FormRowDirective,
    FormControlComponent,
    MkdeDatepickerComponent,
    AutofocusDirective,
    I18nTextPipe,
    I18nTextListPipe,
  ],
})
export class InspectionDialogComponent implements OnInit {
  protected modal = inject(NgbActiveModal);
  private inspectionCategoryCacheService = inject(InspectionCategoryCacheService);
  private enumCacheService = inject(EnumCacheService);
  private inspectionDBService = inject(InspectionDBService);
  private translateService = inject(TranslateService);
  private alerts = inject(MkdeAlertHolderDirective);
  private notDefinedPointsService = inject(StatusChangeValidationService);
  private modalService = inject(NgbModal);

  @Input()
  public inspection!: InspectionWithResultsDto;

  @Input()
  public inspectionCategories!: InspectionElementDto[];

  @ViewChild('form', { static: true })
  public form!: NgForm;

  public inspectionCategoryShortNames: I18NText[] = [];
  public inspectionStyles: InspectionStyleDto[] = [];
  public inspectionStates: InspectionStatusDto[] = [];
  public tmpInspectionStatus?: InspectionStatusDto;
  public displayStatusDingsbums = false;

  public constructor() {
    keysToTranslate([
      'Kontrolle.Datum',
      'Kontrolle.Typ',
      'Kontrolle.Grund',
      'Kontrolle.Stelle',
      'Kontrolle.Art',
      'Kontrolle.Kontrolleur',
      'Kontrolle.Status',
      'Kontrolle.Bemerkung',
      'Die Grunddaten konnten nicht gespeichert werden.',
    ]);
  }

  public ngOnInit(): void {
    if (this.inspection.categoryResults) {
      this.inspectionCategoryShortNames =
        this.inspectionCategoryCacheService.getInspectionCategoryShortNames(
          this.inspection.categoryResults.map((categoryResult) => categoryResult.categoryNumber),
        );
    }
    this.inspectionStyles = this.enumCacheService.getInspectionStyles();
    this.inspectionStates = this.enumCacheService
      .getInspectionStates()
      .filter(
        (status) =>
          status.asEnum === InspectionStatus.Working || status.asEnum === InspectionStatus.Entered,
      );
    this.tmpInspectionStatus = this.inspectionStates.find(
      (inspectionStatus) => inspectionStatus.asEnum === this.inspection.inspectionStatus.asEnum,
    );
  }

  public saveInspection() {
    if (this.form.valid) {
      this.inspectionDBService.putInspectionWithResults(this.inspection).subscribe(
        (success: string) => {
          console.log(success);
        },
        (err: any) => {
          console.log(err);
          this.alerts.danger(
            this.translateService.instant('Die Grunddaten konnten nicht gespeichert werden.'),
            err,
          );
        },
      );
    }
  }

  public changeInspectionStatus() {
    setTimeout(() => {
      if (this.tmpInspectionStatus?.asEnum === InspectionStatus.Entered) {
        const notDefinedPointsMap = this.notDefinedPointsService.getNotDefinedPointsMap(
          this.inspection,
        );
        if (notDefinedPointsMap.size) {
          this.displayStatusDingsbums = true;
          const statusChangeModal = this.modalService.open(StatusChangeDialogComponent, {
            size: 'lg',
            backdrop: 'static',
            keyboard: false,
          });
          statusChangeModal.componentInstance.notDefinedPointsMap = notDefinedPointsMap;
          statusChangeModal.result.then(
            () => {
              this.inspection.inspectionStatus =
                this.tmpInspectionStatus ?? this.inspection.inspectionStatus;
              this.displayStatusDingsbums = false;
              this.saveInspection();
            },
            () => {
              this.displayStatusDingsbums = false;
              this.tmpInspectionStatus = this.inspection.inspectionStatus;
            },
          );
        } else {
          this.inspection.inspectionStatus = this.tmpInspectionStatus;
          this.saveInspection();
        }
      } else {
        this.inspection.inspectionStatus =
          this.tmpInspectionStatus ?? this.inspection.inspectionStatus;
        this.saveInspection();
      }
    }, 0);
  }

  public setInspectionDateToToday() {
    const today = new Date();
    const dateString = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();
    this.form.form.controls.inspectionDate.setValue(dateString);
    this.saveInspection();
  }

  public sameKey = (a: { key: any }, b: { key: any }) => (a && b ? a.key === b.key : a === b);

  public sameEnumValue = (a: { asEnum: any } | null, b: { asEnum: any } | null) =>
    a && b ? a.asEnum === b.asEnum : a === b;
}
